import { useEffect, useMemo, useRef, useState } from "react";
import { analyzeMovementConstraintsPainIssue, fetchExercises } from "../../../services/api";
import {
  AFFECTED_AREA_OPTIONS,
  PAIN_SEVERITY_V2_OPTIONS,
} from "../settingsOptions";
import { Badge, Button, Card, Chip, Dialog } from "../../../design-v2";
import {
  CharacterCount,
  CollapsibleBlock,
  Field,
  INPUT_CLASSES,
  ReadonlyChipList,
  SectionBlock,
  SegmentedSelector,
  TEXTAREA_CLASSES,
  applyDraftUpdate,
  findFieldError,
  formatTokenLabel,
} from "./shared";
import {
  AnalyzeGlyph,
  CheckCircleGlyph,
  ChevronDownGlyph,
  CloseGlyph,
  PlusCircleGlyph,
  PlusGlyph,
  TrashGlyph,
} from "../settingsGlyphs";

const MAX_PAIN_ISSUES = 5;
const MAX_DESCRIPTION_LENGTH = 500;
const ANALYSIS_STATUS_LABELS = {
  draft: "Draft",
  needs_clarification: "Needs clarification",
  analyzed: "Analyzed",
  needs_reanalysis: "Needs re-analysis",
};

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function getMovementConstraints(draft) {
  const movementConstraints = draft?.movementConstraints || {};

  return {
    painIssues: toArray(movementConstraints.painIssues),
    manualBlockedExerciseIds: toArray(movementConstraints.manualBlockedExerciseIds),
  };
}

function buildSummaryItems(values, meta) {
  return toArray(values).map((value) => ({
    label: formatTokenLabel(value),
    meta,
  }));
}

function signalKey(signal) {
  return `${signal?.type || ""}:${signal?.value || ""}`;
}

function isCompleteIssue(issue) {
  return Boolean(
    String(issue?.id || "").trim() &&
      String(issue?.description || "").trim() &&
      String(issue?.affectedArea || "").trim() &&
      String(issue?.painSeverity || "").trim()
  );
}

function getSignalDecision(issue, signal) {
  const key = signalKey(signal);
  const match = toArray(issue?.confirmedSignals).find(
    (confirmedSignal) => signalKey(confirmedSignal) === key
  );

  return match?.decision || "none";
}

function getSignalCautionLevel(issue, signal) {
  const key = signalKey(signal);
  const match = toArray(issue?.confirmedSignals).find(
    (confirmedSignal) => signalKey(confirmedSignal) === key
  );

  return match?.cautionLevel || signal?.cautionLevel || "none";
}

function addUnique(target, value) {
  if (value && !target.includes(value)) {
    target.push(value);
  }
}

function deriveMovementConstraints(movementConstraints) {
  const cautionMovementPatterns = [];
  const blockedMovementPatterns = [];
  const cautionJointStressTags = [];
  const blockedJointStressTags = [];
  const manualBlockedExerciseIds = Array.from(
    new Set(toArray(movementConstraints.manualBlockedExerciseIds).filter(Boolean))
  );

  toArray(movementConstraints.painIssues)
    .filter((issue) => issue.analysisStatus === "analyzed" && isCompleteIssue(issue))
    .forEach((issue) => {
      toArray(issue.confirmedSignals).forEach((signal) => {
        if (signal.type === "movementPattern" && signal.decision === "caution") {
          addUnique(cautionMovementPatterns, signal.value);
        }

        if (signal.type === "movementPattern" && signal.decision === "blocked") {
          addUnique(blockedMovementPatterns, signal.value);
        }

        if (signal.type === "jointStressTag" && signal.decision === "caution") {
          addUnique(cautionJointStressTags, signal.value);
        }

        if (signal.type === "jointStressTag" && signal.decision === "blocked") {
          addUnique(blockedJointStressTags, signal.value);
        }
      });
    });

  return {
    cautionMovementPatterns,
    blockedMovementPatterns,
    cautionJointStressTags,
    blockedJointStressTags,
    manualBlockedExerciseIds,
    blockedExerciseIds: manualBlockedExerciseIds,
    debug: {
      manualBlockedExerciseCount: manualBlockedExerciseIds.length,
      ruleDerivedBlockedExerciseCount: null,
    },
  };
}

function createIssue() {
  const randomSuffix = Math.random().toString(36).slice(2, 8);

  return {
    id: `issue_${Date.now()}_${randomSuffix}`,
    description: "",
    affectedArea: "",
    painSeverity: "low",
    trainingRule: "",
    analysisStatus: "draft",
    clarificationQuestions: [],
    clarificationAnswers: [],
    aiSummary: "",
    detectedSignals: [],
    confirmedSignals: [],
  };
}

function getIssueTitle(issue, index) {
  const description = String(issue?.description || "").trim();
  if (!description) {
    return `Pain issue ${index + 1}`;
  }

  return description.length > 48 ? `${description.slice(0, 45)}...` : description;
}

function getOptionLabel(options, value, fallback = "Not set") {
  return options.find((option) => option.value === value)?.label || fallback;
}

function getIssueMeta(issue) {
  return [
    getOptionLabel(AFFECTED_AREA_OPTIONS, issue?.affectedArea),
    getOptionLabel(PAIN_SEVERITY_V2_OPTIONS, issue?.painSeverity),
  ].join(" · ");
}

function getSignalCounts(issue) {
  return toArray(issue?.confirmedSignals).reduce(
    (counts, signal) => ({
      monitor: counts.monitor + (signal.decision === "monitor" ? 1 : 0),
      caution: counts.caution + (signal.decision === "caution" ? 1 : 0),
      blocked: counts.blocked + (signal.decision === "blocked" ? 1 : 0),
    }),
    { monitor: 0, caution: 0, blocked: 0 }
  );
}

function normalizeExerciseName(exercise) {
  return exercise?.name || exercise?.exerciseId || "";
}

function SummaryChipGroup({ title, items, tone = "neutral", emptyLabel }) {
  if (!items.length && emptyLabel) {
    return (
      <div className="min-w-0">
        {title ? <p className="lz-settings-label">{title}</p> : null}
        <p className="lz-settings-hint">{emptyLabel}</p>
      </div>
    );
  }

  if (!items.length) {
    return null;
  }

  return (
    <div className="min-w-0">
      <p className="lz-settings-label">{title}</p>
      <div className="lz-settings-chip-row mt-2">
        {items.map((item) => (
          <Badge
            key={`${title}-${item.meta || ""}-${item.label}`}
            tone={tone === "danger" ? "danger" : "info"}
          >
            <span>{item.label}</span>
            {item.meta ? <span className="lz-settings-badge-meta">{item.meta}</span> : null}
          </Badge>
        ))}
      </div>
    </div>
  );
}

export default function MovementConstraintsSection({
  draft,
  onChange,
  fieldErrors,
  presentation = "settings",
  allowMovementAnalysis = true,
}) {
  const isOnboarding = presentation === "onboarding";
  const movementConstraints = getMovementConstraints(draft);
  const exerciseSearchRef = useRef(null);
  const issueRefs = useRef({});
  const [openIssueId, setOpenIssueId] = useState(null);
  const [pendingRemoveIssueId, setPendingRemoveIssueId] = useState(null);
  const [pendingReanalyzeIssueId, setPendingReanalyzeIssueId] = useState(null);
  const [exerciseQuery, setExerciseQuery] = useState("");
  const [exerciseResults, setExerciseResults] = useState([]);
  const [exerciseNamesById, setExerciseNamesById] = useState({});
  const [isSearchingExercises, setIsSearchingExercises] = useState(false);
  const [exerciseSearchError, setExerciseSearchError] = useState("");
  const [analyzingIssueId, setAnalyzingIssueId] = useState(null);
  const [analysisErrorsByIssueId, setAnalysisErrorsByIssueId] = useState({});
  const derivedConstraints = useMemo(
    () => deriveMovementConstraints(movementConstraints),
    [movementConstraints]
  );
  const restrictedItems = [
    ...buildSummaryItems(derivedConstraints.blockedMovementPatterns, "Movement"),
    ...buildSummaryItems(derivedConstraints.blockedJointStressTags, "Stress"),
  ];
  const cautionItems = [
    ...buildSummaryItems(derivedConstraints.cautionMovementPatterns, "Movement"),
    ...buildSummaryItems(derivedConstraints.cautionJointStressTags, "Stress"),
  ];
  const hasActiveConstraints =
    restrictedItems.length > 0 ||
    cautionItems.length > 0 ||
    derivedConstraints.manualBlockedExerciseIds.length > 0;
  const canAddIssue = movementConstraints.painIssues.length < MAX_PAIN_ISSUES;
  const pendingRemoveIssue = movementConstraints.painIssues.find(
    (issue) => issue.id === pendingRemoveIssueId
  );
  const pendingReanalyzeIssue = movementConstraints.painIssues.find(
    (issue) => issue.id === pendingReanalyzeIssueId
  );

  useEffect(() => {
    function handleDocumentPointerDown(event) {
      if (exerciseSearchRef.current?.contains(event.target)) {
        return;
      }

      clearManualExerciseSearch();
    }

    document.addEventListener("pointerdown", handleDocumentPointerDown);

    return () => {
      document.removeEventListener("pointerdown", handleDocumentPointerDown);
    };
  }, []);

  useEffect(() => {
    const query = exerciseQuery.trim();
    if (!query) {
      setExerciseResults([]);
      setExerciseSearchError("");
      setIsSearchingExercises(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setIsSearchingExercises(true);
      setExerciseSearchError("");

      try {
        const results = await fetchExercises({ q: query, limit: 25, status: "approved" });

        if (!cancelled) {
          setExerciseResults(results);
          setExerciseNamesById((currentNames) => {
            const nextNames = { ...currentNames };
            results.forEach((exercise) => {
              if (exercise?.exerciseId) {
                nextNames[exercise.exerciseId] = normalizeExerciseName(exercise);
              }
            });
            return nextNames;
          });
        }
      } catch (error) {
        if (!cancelled) {
          setExerciseResults([]);
          setExerciseSearchError(error.message || "Unable to load exercises.");
        }
      } finally {
        if (!cancelled) {
          setIsSearchingExercises(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [exerciseQuery]);

  function updateMovementConstraints(producer) {
    applyDraftUpdate(draft, onChange, (nextDraft) => {
      if (!nextDraft.movementConstraints || typeof nextDraft.movementConstraints !== "object") {
        nextDraft.movementConstraints = {};
      }

      if (!Array.isArray(nextDraft.movementConstraints.painIssues)) {
        nextDraft.movementConstraints.painIssues = [];
      }

      if (!Array.isArray(nextDraft.movementConstraints.manualBlockedExerciseIds)) {
        nextDraft.movementConstraints.manualBlockedExerciseIds = [];
      }

      producer(nextDraft.movementConstraints);
    });
  }

  function updateIssue(issueId, producer) {
    updateMovementConstraints((nextMovementConstraints) => {
      nextMovementConstraints.painIssues = nextMovementConstraints.painIssues.map((issue) => {
        if (issue.id !== issueId) {
          return issue;
        }

        const nextIssue = { ...issue };
        producer(nextIssue);
        return nextIssue;
      });
    });
  }

  function handleAddIssue() {
    if (!canAddIssue) {
      return;
    }

    const issue = createIssue();
    updateMovementConstraints((nextMovementConstraints) => {
      nextMovementConstraints.painIssues = [...nextMovementConstraints.painIssues, issue];
    });
    setOpenIssueId(issue.id);
    window.requestAnimationFrame(() => {
      const issueElement = issueRefs.current[issue.id];
      if (typeof issueElement?.scrollIntoView !== "function") {
        return;
      }

      issueElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }

  function handleRemoveIssue(issueId) {
    updateMovementConstraints((nextMovementConstraints) => {
      nextMovementConstraints.painIssues = nextMovementConstraints.painIssues.filter(
        (issue) => issue.id !== issueId
      );
    });
    setOpenIssueId((currentOpenIssueId) =>
      currentOpenIssueId === issueId ? null : currentOpenIssueId
    );
    setPendingRemoveIssueId(null);
  }

  function clearManualExerciseSearch() {
    setExerciseQuery("");
    setExerciseResults([]);
    setExerciseSearchError("");
    setIsSearchingExercises(false);
  }

  function handleSourceFieldChange(issueId, field, value) {
    updateIssue(issueId, (nextIssue) => {
      nextIssue[field] = value;

      if (nextIssue.analysisStatus === "analyzed") {
        nextIssue.analysisStatus = "needs_reanalysis";
      }
    });
  }

  function handleClarificationAnswerChange(issueId, questionId, answer) {
    updateIssue(issueId, (nextIssue) => {
      const existingAnswers = toArray(nextIssue.clarificationAnswers).filter(
        (clarificationAnswer) => clarificationAnswer.questionId !== questionId
      );
      const normalizedAnswer = String(answer || "");

      nextIssue.clarificationAnswers = normalizedAnswer.trim()
        ? [...existingAnswers, { questionId, answer: normalizedAnswer }]
        : existingAnswers;
    });
  }

  async function runAnalyze(issue) {
    if (!allowMovementAnalysis || !isCompleteIssue(issue)) {
      return;
    }

    setAnalyzingIssueId(issue.id);
    setAnalysisErrorsByIssueId((currentErrors) => ({
      ...currentErrors,
      [issue.id]: "",
    }));

    try {
      const result = await analyzeMovementConstraintsPainIssue({
        painIssue: {
          id: issue.id,
          description: issue.description,
          affectedArea: issue.affectedArea,
          painSeverity: issue.painSeverity,
          clarificationAnswers: toArray(issue.clarificationAnswers),
        },
        context: {
          existingPainIssues: movementConstraints.painIssues.filter(
            (candidateIssue) => candidateIssue.id !== issue.id
          ),
          manualBlockedExerciseIds: movementConstraints.manualBlockedExerciseIds,
        },
      });

      updateIssue(issue.id, (nextIssue) => {
        nextIssue.analysisStatus = result.status;
        nextIssue.clarificationQuestions = toArray(result.clarificationQuestions);
        nextIssue.aiSummary = result.aiSummary || "";

        if (result.status === "analyzed") {
          nextIssue.detectedSignals = toArray(result.detectedSignals);
          nextIssue.confirmedSignals = [];
        }
      });
      setPendingReanalyzeIssueId(null);
    } catch (error) {
      setAnalysisErrorsByIssueId((currentErrors) => ({
        ...currentErrors,
        [issue.id]:
          error?.code === "AI_MOVEMENT_CONSTRAINTS_DISABLED"
            ? "AI analysis is not enabled yet."
            : error?.code === "AI_ANALYZE_RATE_LIMITED"
              ? "Please wait a few seconds before running another AI analysis."
            : error?.message || "Unable to analyze this pain issue.",
      }));
    } finally {
      setAnalyzingIssueId((currentIssueId) => (currentIssueId === issue.id ? null : currentIssueId));
    }
  }

  function handleAnalyze(issue) {
    const isReanalysis =
      issue.analysisStatus === "analyzed" || issue.analysisStatus === "needs_reanalysis";

    if (isReanalysis) {
      setPendingReanalyzeIssueId(issue.id);
      return;
    }

    runAnalyze(issue);
  }

  function handleDecisionChange(issue, signal, decision) {
    updateIssue(issue.id, (nextIssue) => {
      const key = signalKey(signal);
      const remainingSignals = toArray(nextIssue.confirmedSignals).filter(
        (confirmedSignal) => signalKey(confirmedSignal) !== key
      );

      nextIssue.confirmedSignals =
        decision === "none"
          ? remainingSignals
          : [
              ...remainingSignals,
              {
                type: signal.type,
                value: signal.value,
                decision,
                cautionLevel: decision === "caution" ? signal.cautionLevel || "medium" : "none",
              },
            ];
    });
  }

  function handleAddManualBlockedExercise(exercise) {
    if (!exercise?.exerciseId) {
      return;
    }

    setExerciseNamesById((currentNames) => ({
      ...currentNames,
      [exercise.exerciseId]: normalizeExerciseName(exercise),
    }));
    updateMovementConstraints((nextMovementConstraints) => {
      nextMovementConstraints.manualBlockedExerciseIds = Array.from(
        new Set([...nextMovementConstraints.manualBlockedExerciseIds, exercise.exerciseId])
      );
    });
    clearManualExerciseSearch();
  }

  function handleRemoveManualBlockedExercise(exerciseId) {
    updateMovementConstraints((nextMovementConstraints) => {
      nextMovementConstraints.manualBlockedExerciseIds =
        nextMovementConstraints.manualBlockedExerciseIds.filter((id) => id !== exerciseId);
    });
  }

  function renderIssueForm(issue, index) {
    const description = String(issue.description || "");
    const canAnalyze =
      allowMovementAnalysis &&
      isCompleteIssue(issue) && description.length <= MAX_DESCRIPTION_LENGTH;
    const isAnalyzing = analyzingIssueId === issue.id;
    const analysisError = analysisErrorsByIssueId[issue.id] || "";
    const analyzeLabel = issue.analysisStatus === "analyzed"
      || issue.analysisStatus === "needs_reanalysis"
      ? isOnboarding ? "Review again" : "Re-analyze with AI"
      : isOnboarding ? "Review with AI" : "Analyze with AI";

    return (
      <div className="space-y-5">
        <Field
          label={isOnboarding ? "What are you experiencing?" : "Description"}
          error={findFieldError(fieldErrors, [
            `movementConstraints.painIssues[${index}].description`,
          ])}
        >
          <textarea
            rows={4}
            className={`${TEXTAREA_CLASSES} mt-2`}
            value={description}
            onChange={(event) =>
              handleSourceFieldChange(issue.id, "description", event.target.value)
            }
            placeholder="Shoulder irritation during overhead pressing"
          />
          <CharacterCount current={description.length} limit={MAX_DESCRIPTION_LENGTH} />
        </Field>

        <Field
          label="Affected Area"
          error={findFieldError(fieldErrors, [
            `movementConstraints.painIssues[${index}].affectedArea`,
          ])}
        >
          <div className="lz-settings-chip-row mt-2">
            {AFFECTED_AREA_OPTIONS.map((option) => (
              <Chip
                key={option.value}
                selected={issue.affectedArea === option.value}
                onClick={() => handleSourceFieldChange(issue.id, "affectedArea", option.value)}
              >
                {option.label}
              </Chip>
            ))}
          </div>
        </Field>

        <Field
          label={isOnboarding ? "Pain level" : "Pain Severity"}
          error={findFieldError(fieldErrors, [
            `movementConstraints.painIssues[${index}].painSeverity`,
          ])}
        >
          <div className="mt-2">
            <SegmentedSelector
              label={isOnboarding ? "Pain level" : "Pain Severity"}
              options={PAIN_SEVERITY_V2_OPTIONS}
              value={issue.painSeverity || ""}
              onChange={(value) => handleSourceFieldChange(issue.id, "painSeverity", value)}
            />
          </div>
        </Field>

        {toArray(issue.clarificationQuestions).length ? (
          <Field label={isOnboarding ? "A few follow-up questions" : "Clarification Questions"}>
            <div className="mt-2 space-y-3">
              {toArray(issue.clarificationQuestions).map((question) => {
                const answer =
                  toArray(issue.clarificationAnswers).find(
                    (candidateAnswer) => candidateAnswer.questionId === question.id
                  )?.answer || "";

                return (
                  <Card key={question.id} tone="raised" padding="sm">
                    <p className="lz-settings-primary-text">{question.question}</p>
                    <input
                      type="text"
                      className={`${INPUT_CLASSES} mt-2`}
                      value={answer}
                      onChange={(event) =>
                        handleClarificationAnswerChange(issue.id, question.id, event.target.value)
                      }
                      placeholder="Answer briefly..."
                    />
                  </Card>
                );
              })}
            </div>
          </Field>
        ) : null}

        {issue.aiSummary ? (
          <Field label={isOnboarding ? "Review summary" : "AI Summary"}>
            <Card tone="raised" padding="sm" className="mt-2">
              <p className="lz-settings-muted-text">{issue.aiSummary}</p>
            </Card>
          </Field>
        ) : null}

        <div className="flex justify-end">
          <div className="text-right">
            <Button
              type="button"
              variant="secondary"
              aria-label={analyzeLabel}
              disabled={!canAnalyze || isAnalyzing}
              onClick={() => handleAnalyze(issue)}
            >
              <AnalyzeGlyph />
              {isAnalyzing ? "Analyzing..." : analyzeLabel}
            </Button>
            {analysisError ? (
              <p className="lz-settings-error">{analysisError}</p>
            ) : !allowMovementAnalysis ? (
              <p className="lz-settings-note">
                AI review is available after account creation.
              </p>
            ) : (
              <p className="lz-settings-note">
                This is not medical advice. Review every suggested training signal before applying it.
              </p>
            )}
          </div>
        </div>

        <Field
          label={isOnboarding ? "Suggested adjustments" : "Detected Signals"}
          hint={isOnboarding
            ? "Choose which adjustments should shape your training."
            : "Suggested training signals. Only confirmed decisions become active."}
        >
          {toArray(issue.detectedSignals).length ? (
            <div className="mt-2 space-y-2">
              {issue.detectedSignals.map((signal) => {
                const decision = getSignalDecision(issue, signal);
                const cautionLevel = getSignalCautionLevel(issue, signal);

                return (
                  <div key={signalKey(signal)} className="lz-settings-signal">
                    <div className="flex flex-col gap-3">
                      <div className="min-w-0">
                        <p className="lz-settings-primary-text">
                          {formatTokenLabel(signal.value)}
                        </p>
                        <div className="lz-settings-chip-row mt-1">
                          <Badge tone="neutral">
                            {signal.type === "movementPattern" ? "Movement" : "Joint stress"}
                          </Badge>
                          {signal.recommendedDecision ? (
                            <Badge tone="action">
                              AI: {formatTokenLabel(signal.recommendedDecision)}
                            </Badge>
                          ) : null}
                          {signal.cautionLevel && signal.cautionLevel !== "none" ? (
                            <Badge tone="info">{signal.cautionLevel} caution</Badge>
                          ) : null}
                          {signal.confidence ? (
                            <Badge tone="neutral">{signal.confidence} confidence</Badge>
                          ) : null}
                        </div>
                        {signal.reason ? (
                          <p className="lz-settings-note">{signal.reason}</p>
                        ) : null}
                      </div>
                      <div className="lz-settings-decisions">
                        {[
                          ["none", "Ignore"],
                          ["monitor", "Monitor"],
                          ["caution", "Caution"],
                          ["blocked", "Blocked"],
                        ].map(([value, label]) => (
                          <button
                            key={value}
                            type="button"
                            aria-pressed={decision === value}
                            onClick={() => handleDecisionChange(issue, signal, value)}
                            className={[
                              "lz-settings-decisions__option",
                              decision === value
                                ? value === "blocked"
                                  ? "lz-settings-decisions__option--selected-blocked"
                                  : value === "caution"
                                    ? "lz-settings-decisions__option--selected-caution"
                                    : "lz-settings-decisions__option--selected"
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            title={
                              value === "caution" && cautionLevel !== "none"
                                ? `Caution level: ${cautionLevel}`
                                : undefined
                            }
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="lz-settings-hint">
              {isOnboarding ? "No suggested adjustments yet." : "No detected signals yet."}
            </p>
          )}
        </Field>
      </div>
    );
  }

  return (
    <div className={isOnboarding ? "lz-v2-movement-constraints space-y-6" : "space-y-6"}>
      <SectionBlock
        title={isOnboarding ? "Current adjustments" : "Active Constraints"}
        description={isOnboarding ? "Training limits currently in place." : "Current applied movement limits."}
        className="lz-settings-block--info"
      >
        <div className="space-y-3">
          {hasActiveConstraints ? (
            <div className="space-y-3">
              <SummaryChipGroup
                title={isOnboarding ? "Avoid" : "Blocked signals"}
                items={restrictedItems}
                tone="danger"
              />
              <SummaryChipGroup
                title={isOnboarding ? "Use caution" : "Caution signals"}
                items={cautionItems}
              />
              {derivedConstraints.manualBlockedExerciseIds.length ? (
                <SummaryChipGroup
                  title={isOnboarding ? "Exercises to avoid" : "Manual blocked exercises"}
                  items={[
                    {
                      label: `${derivedConstraints.manualBlockedExerciseIds.length}`,
                      meta: "Exercises",
                    },
                  ]}
                  tone="danger"
                />
              ) : null}
            </div>
          ) : (
            <SummaryChipGroup
              items={[]}
              emptyLabel={isOnboarding ? "No adjustments yet" : "No active movement restrictions"}
            />
          )}
        </div>
      </SectionBlock>

      <SectionBlock
        title={isOnboarding ? "Your limitations" : "Pain Issues"}
        description={isOnboarding ? "Add each issue separately." : "Track each issue separately."}
      >
        <div className="space-y-3">
          {movementConstraints.painIssues.length ? (
            movementConstraints.painIssues.map((issue, index) => {
              const isOpen = openIssueId === issue.id;
              const counts = getSignalCounts(issue);

              return (
                <div
                  key={issue.id}
                  ref={(element) => {
                    if (element) {
                      issueRefs.current[issue.id] = element;
                    } else {
                      delete issueRefs.current[issue.id];
                    }
                  }}
                  className={[
                    "lz-settings-collapsible lz-settings-issue",
                    isOpen ? "lz-settings-collapsible--open" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label={`Toggle pain issue ${index + 1}`}
                      aria-expanded={isOpen}
                      aria-controls={`lz-settings-issue-panel-${issue.id}`}
                      onClick={() => setOpenIssueId(isOpen ? null : issue.id)}
                      className="lz-settings-collapsible__trigger"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="lz-settings-collapsible__title block">
                          {getIssueTitle(issue, index)}
                        </span>
                        <span className="lz-settings-chip-row mt-2">
                          <Badge tone="neutral">
                            {ANALYSIS_STATUS_LABELS[issue.analysisStatus || "draft"]}
                          </Badge>
                          {counts.caution ? (
                            <Badge tone="info">{counts.caution} caution</Badge>
                          ) : null}
                          {counts.monitor ? (
                            <Badge tone="neutral">{counts.monitor} monitor</Badge>
                          ) : null}
                          {counts.blocked ? (
                            <Badge tone="danger">{counts.blocked} blocked</Badge>
                          ) : null}
                        </span>
                        <span className="lz-settings-section__description block">
                          {getIssueMeta(issue)}
                        </span>
                      </span>

                      <ChevronDownGlyph className="lz-settings-collapsible__icon" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingRemoveIssueId(issue.id)}
                      className="lz-settings-issue__remove"
                      aria-label={`Remove ${getIssueTitle(issue, index)}`}
                    >
                      <TrashGlyph />
                    </button>
                  </div>
                  {isOpen ? (
                    <div
                      className="lz-settings-issue__panel"
                      id={`lz-settings-issue-panel-${issue.id}`}
                    >
                      {renderIssueForm(issue, index)}
                    </div>
                  ) : null}
                </div>
              );
            })
          ) : (
            <p className="lz-settings-hint">
              {isOnboarding ? "No limitations added yet." : "No pain issues added."}
            </p>
          )}

          <button
            type="button"
            disabled={!canAddIssue}
            onClick={handleAddIssue}
            className="lz-settings-add-button"
          >
            <PlusGlyph />
            {isOnboarding ? "Add a limitation" : "Add pain issue"}
          </button>
          {!canAddIssue ? (
            <p className="lz-settings-hint">
              You can add up to {MAX_PAIN_ISSUES} pain issues.
            </p>
          ) : null}
        </div>
      </SectionBlock>

      <SectionBlock
        title={isOnboarding ? "Exercises to avoid" : "Manual Blocked Exercises"}
        description={isOnboarding
          ? "Optionally block a specific exercise."
          : "Block exercises you do not want, independent of pain issues."}
      >
        <div ref={exerciseSearchRef}>
          <Field label={isOnboarding ? "Find an exercise" : "Search Exercises"}>
            <div className="lz-settings-search mt-2">
              <input
                type="text"
                className={INPUT_CLASSES}
                value={exerciseQuery}
                onChange={(event) => setExerciseQuery(event.target.value)}
                placeholder="Search exercises..."
              />
              {exerciseQuery ? (
                <button
                  type="button"
                  aria-label="Clear exercise search"
                  onClick={clearManualExerciseSearch}
                  className="lz-settings-icon-button lz-settings-search__clear"
                >
                  <CloseGlyph />
                </button>
              ) : null}
            </div>
          </Field>

          {exerciseQuery.trim() ? (
            <div className="lz-settings-results">
              {isSearchingExercises ? (
                <p className="lz-settings-muted-text px-2 py-3">Loading exercises...</p>
              ) : null}
              {!isSearchingExercises && exerciseSearchError ? (
                <p className="lz-settings-error px-2 py-3">{exerciseSearchError}</p>
              ) : null}
              {!isSearchingExercises && !exerciseSearchError && exerciseResults.length === 0 ? (
                <p className="lz-settings-muted-text px-2 py-3">No exercises found.</p>
              ) : null}
              {!isSearchingExercises &&
                !exerciseSearchError &&
                exerciseResults.map((exercise) => {
                  const isSelected = movementConstraints.manualBlockedExerciseIds.includes(
                    exercise.exerciseId
                  );

                  return (
                    <button
                      key={exercise.exerciseId}
                      type="button"
                      disabled={isSelected}
                      onClick={() => handleAddManualBlockedExercise(exercise)}
                      className="lz-settings-result-row"
                    >
                      <span className="min-w-0">
                        <span className="lz-settings-primary-text block">
                          {exercise.name}
                        </span>
                        <span className="lz-settings-secondary-text block">
                          {exercise.exerciseId}
                        </span>
                      </span>
                      {isSelected ? (
                        <CheckCircleGlyph className="lz-settings-row__chevron" />
                      ) : (
                        <PlusCircleGlyph className="lz-settings-row__chevron" />
                      )}
                    </button>
                  );
                })}
            </div>
          ) : null}
        </div>

        <Field label={isOnboarding ? "Exercises you want to avoid" : "Blocked Exercises"}>
          {movementConstraints.manualBlockedExerciseIds.length ? (
            <div className="mt-2 space-y-2">
              {movementConstraints.manualBlockedExerciseIds.map((exerciseId) => (
                <div key={exerciseId} className="lz-settings-blocked-row">
                  <div className="min-w-0">
                    <p className="lz-settings-primary-text">
                      {exerciseNamesById[exerciseId] || exerciseId}
                    </p>
                    <p className="lz-settings-secondary-text">{exerciseId}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveManualBlockedExercise(exerciseId)}
                    className="lz-settings-icon-button"
                    aria-label={`Remove ${exerciseId}`}
                  >
                    <CloseGlyph />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="lz-settings-hint">No manually blocked exercises.</p>
          )}
        </Field>
      </SectionBlock>

      {/*
        Canonical Dialog: same open condition (a pending removal), same Cancel
        and Remove outcomes, and the same resulting pain-issue state as the
        previous ad-hoc overlay.
      */}
      <Dialog
        size="sm"
        open={Boolean(pendingRemoveIssue)}
        onClose={() => setPendingRemoveIssueId(null)}
        title="Remove this pain issue?"
        closeLabel="Close remove pain issue dialog"
        footer={
          <>
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setPendingRemoveIssueId(null)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              onClick={() => handleRemoveIssue(pendingRemoveIssue.id)}
            >
              Remove
            </Button>
          </>
        }
      >
        <p className="lz-settings-muted-text">
          This will remove its detected and confirmed signals. This action cannot be undone.
        </p>
      </Dialog>

      {/*
        Canonical Dialog: same open condition (a pending re-analysis), same
        Cancel and confirm outcomes, and the same analysis call as before.
      */}
      <Dialog
        size="sm"
        open={Boolean(pendingReanalyzeIssue)}
        onClose={() => setPendingReanalyzeIssueId(null)}
        title={isOnboarding ? "Review this limitation again?" : "Re-analyze this pain issue?"}
        closeLabel="Close re-analyze pain issue dialog"
        footer={
          <>
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setPendingReanalyzeIssueId(null)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={() => runAnalyze(pendingReanalyzeIssue)}
              disabled={analyzingIssueId === pendingReanalyzeIssue?.id}
            >
              {analyzingIssueId === pendingReanalyzeIssue?.id
                ? "Analyzing..."
                : isOnboarding ? "Review again" : "Re-analyze with AI"}
            </Button>
          </>
        }
      >
        <p className="lz-settings-muted-text">
          {isOnboarding
            ? "We’ll request a fresh review. Your current choices stay in place until it finishes."
            : "This will request a fresh analysis. Confirmed decisions stay in place unless the new analysis returns final detected signals."}
        </p>
      </Dialog>

      {!isOnboarding ? (
        <CollapsibleBlock
          title="Advanced signals"
          description="Readonly overview of applied movement constraints."
          badge="Readonly"
          defaultOpen={false}
        >
          <div className="space-y-4">
          <Field label="Caution Movement Patterns">
            <ReadonlyChipList
              values={derivedConstraints.cautionMovementPatterns}
              labelMap={{}}
              emptyLabel="None"
            />
          </Field>
          <Field label="Blocked Movement Patterns">
            <ReadonlyChipList
              values={derivedConstraints.blockedMovementPatterns}
              labelMap={{}}
              emptyLabel="None"
              tone="strong"
            />
          </Field>
          <Field label="Caution Joint Stress Tags">
            <ReadonlyChipList
              values={derivedConstraints.cautionJointStressTags}
              labelMap={{}}
              emptyLabel="None"
            />
          </Field>
          <Field label="Blocked Joint Stress Tags">
            <ReadonlyChipList
              values={derivedConstraints.blockedJointStressTags}
              labelMap={{}}
              emptyLabel="None"
              tone="strong"
            />
          </Field>
          <Field label="Manual Blocked Exercise IDs">
            <ReadonlyChipList
              values={derivedConstraints.manualBlockedExerciseIds}
              labelMap={{}}
              emptyLabel="None"
            />
          </Field>
          <Field label="Debug Counts">
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Card tone="raised" padding="sm">
                <p className="lz-settings-label">Manual blocked</p>
                <p className="lz-settings-primary-text">
                  {derivedConstraints.debug.manualBlockedExerciseCount}
                </p>
              </Card>
              <Card tone="raised" padding="sm">
                <p className="lz-settings-label">Rule derived</p>
                <p className="lz-settings-primary-text">
                  {derivedConstraints.debug.ruleDerivedBlockedExerciseCount ?? "Not calculated"}
                </p>
              </Card>
            </div>
          </Field>
          </div>
        </CollapsibleBlock>
      ) : null}
    </div>
  );
}
